import { Helmet } from 'react-helmet-async';

type SEOProps = {
  title?: string;
  description?: string;
  noindex?: boolean;
  image?: string;
  lang?: string;
  canonicalPath?: string;
};

const SITE_NAME = 'InterviewGator';
const SITE_URL = 'https://interviewgator.com';
const DEFAULT_DESCRIPTION = "AI interview practice that doesn't go easy on you";
// Keep the ?v= in sync with the static tags in index.html — scrapers cache
// og:image aggressively and only refetch when the URL changes.
const OG_IMAGE_VERSION = '20260714';
const DEFAULT_LANG = 'en';

function defaultImageForLang(lang: string) {
  const base = lang.toLowerCase().startsWith('pl') ? '/og-image-pl.png' : '/og-image.png';
  return `${base}?v=${OG_IMAGE_VERSION}`;
}

function withSiteName(title?: string) {
  if (!title) return SITE_NAME;
  if (title.includes(SITE_NAME)) return title;
  return `${title} | ${SITE_NAME}`;
}

function buildCanonical(path?: string) {
  const resolvedPath = path ?? (typeof window !== 'undefined' ? window.location.pathname : '/');
  const normalized = resolvedPath.startsWith('/') ? resolvedPath : `/${resolvedPath}`;
  return `${SITE_URL}${normalized}`;
}

export function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  noindex = false,
  image,
  lang = DEFAULT_LANG,
  canonicalPath,
}: SEOProps) {
  const fullTitle = withSiteName(title);
  const canonical = buildCanonical(canonicalPath);
  const robots = noindex ? 'noindex,nofollow' : 'index,follow';
  const resolvedImage = image ?? defaultImageForLang(lang);
  const imageUrl = resolvedImage.startsWith('http') ? resolvedImage : `${SITE_URL}${resolvedImage}`;

  return (
    <Helmet>
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canonical} />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={imageUrl} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
    </Helmet>
  );
}
